import { AxiosError } from 'axios';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { CreditStandingOrderDetailsDtoIn } from '@/models/StandingOrderDetails';
import {
  UNLIMITED_PAYMENTS,
  formatIsoDate,
  getActiveStatus,
  isUnlimited,
  parseDate,
} from './standingOrderModel';
import { isAptOrder } from './aptStandingOrders';
import { AptOrderState, bankActionUrl } from './bankStandingOrderActions';

/*
 * HAT-1317: freezing, reactivating, editing and cancelling a credit standing order from the
 * board. The rules here are the server's (NedarimStandingOrdersService.PlanCreditOrderUpdate),
 * repeated so the form can say what is wrong before anything is sent. The server checks them
 * again either way.
 */

/** Must match FeatureFlagKeys.StandingOrderActions and SeedFeatureFlag_StandingOrderActions.sql. */
export const STANDING_ORDER_ACTIONS_FEATURE_KEY = 'standing-order-actions';

export type StandingOrderActionKind = 'freeze' | 'reactivate' | 'cancel' | 'edit';

/** What the last action did to the order open in the blade, said there until dismissed. */
export interface OrderActionNotice {
  orderId: number;
  kind: 'freeze' | 'reactivate' | 'edit';
  /** dd/MM/yyyy - the next charge Nedarim set on a reactivation. */
  nextDate?: string | null;
}

/** What the action log can hold: the four actions, and the one-off charge that was logged before them. */
export type LoggedActionKind = 'charge' | StandingOrderActionKind;

export interface StandingOrderActionResult {
  success: boolean;
  /** Nedarim's own wording when they refused. */
  message: string | null;
  /** dd/MM/yyyy; set on a reactivation. */
  nextDate: string | null;
  /** A bank order in APT (HAT-1441): the order as it now reads, before the board's list catches up. */
  order?: AptOrderState | null;
}

export interface StandingOrderActionLogEntry {
  action: string;
  /** UTC, with its Z. */
  requestedAt: string;
  requestedBy: string | null;
  succeeded: boolean;
  message: string | null;
  amount: number;
  /** 1 = ILS, 2 = USD. */
  currency: number;
  /** An edit's before and after: {"amount":[350,280],"nextDate":[...],"remainingPayments":[...],"comments":[...]}. */
  details: string | null;
}

export interface UpdateCreditStandingOrderRequest {
  amount?: number;
  /** yyyy-MM-dd */
  nextDate?: string;
  remainingPayments?: number;
  unlimitedPayments?: boolean;
  comments?: string;
}

/** Nedarim's KevaStatus, read live with the order's details: 1 active, 2 frozen, 3 deleted. */
export const KEVA_STATUS_ACTIVE = 1;
export const KEVA_STATUS_FROZEN = 2;
export const KEVA_STATUS_DELETED = 3;

export type OrderState = 'enabled' | 'disabled' | 'ended' | 'cancelled';

/**
 * Where the order stands. The board's row says Enabled yes/no and can be five minutes old; the
 * details are read live and carry Nedarim's own status, so when they are loaded they win - that
 * is how a frozen order is told from a deleted one. An order with no payments left has ended
 * whatever else is said about it.
 */
export function getOrderState(order: NedarimStandingOrderDtoIn, liveStatus?: number | null): OrderState {
  if (liveStatus === KEVA_STATUS_DELETED) return 'cancelled';

  const active = getActiveStatus(order);
  if (active === 'ended') return 'ended';
  if (liveStatus === KEVA_STATUS_FROZEN) return 'disabled';
  if (liveStatus === KEVA_STATUS_ACTIVE) return 'enabled';
  // An accounting-system order awaiting the bank (HAT-1135) is live for the purpose of this
  // state; no action is ever offered on such an order anyway.
  return active === 'awaitingBank' ? 'enabled' : active;
}

/** Freeze and reactivate exist for a live order only: an ended one has nothing to stop or restart. */
export const canToggle = (state: OrderState): boolean => state === 'enabled' || state === 'disabled';

export const canEdit = (state: OrderState): boolean => state !== 'cancelled';

export const canCancel = (state: OrderState): boolean => state !== 'cancelled';

/**
 * The parent mosad and the "אונליין" mosad hold the orders of every fund, and the only thing that
 * ties one of them to this fund is the fund's number written in its note. On those the note is not
 * editable from the board.
 */
export const isSharedMosad = (order: NedarimStandingOrderDtoIn): boolean => order.mosadSource !== 'FundMosad';

export function actionUrl(fundId: number, order: NedarimStandingOrderDtoIn, kind: StandingOrderActionKind): string {
  const path = kind === 'edit' ? 'update' : kind;
  // A bank order the office keeps in APT is changed in APT (HAT-1441), not at Nedarim.
  if (isAptOrder(order)) return bankActionUrl(fundId, order.standingOrderId, path);
  return `nedarim-standing-orders/${fundId}/credit/${order.standingOrderId}/${path}?mosadSource=${encodeURIComponent(order.mosadSource)}`;
}

export const actionLogUrl = (fundId: number, order: NedarimStandingOrderDtoIn): string =>
  isAptOrder(order)
    ? bankActionUrl(fundId, order.standingOrderId, 'actions')
    : `nedarim-standing-orders/${fundId}/credit/${order.standingOrderId}/actions`;

/** The query key the blade reads the order's details under - dropped after an action changes them. */
export const detailsUrl = (fundId: number, order: NedarimStandingOrderDtoIn): string =>
  `nedarim-standing-orders/${fundId}/credit/${order.standingOrderId}/details?mosadSource=${order.mosadSource}`;

/**
 * The words for a failed call: the server's own refusal code when it sent one, otherwise what the
 * status says. Nedarim's refusals are not errors - they come back as success: false - and are
 * shown in Nedarim's words by the caller.
 */
export function describeActionError(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
  bank = false,
): string {
  const response = (error as AxiosError<{ code?: string } | string>)?.response;
  const code = typeof response?.data === 'object' && response?.data ? response.data.code : undefined;

  if (code) {
    // A bank order's refusal has its own words where the credit ones would mislead (HAT-1441: an
    // ended bank order is renewed by the office, not by adding payments here).
    const keys = bank
      ? [`standingOrders.orderActions.bank.rejections.${code}`, `standingOrders.orderActions.rejections.${code}`]
      : [`standingOrders.orderActions.rejections.${code}`];
    for (const key of keys) {
      const text = t(key);
      if (text !== key) return text;
    }
  }

  switch (response?.status) {
    case 403: return t('standingOrders.orderActions.errors.forbidden');
    case 404: return t('standingOrders.orderActions.errors.notFound');
    case 429: return t('standingOrders.orderActions.errors.rateLimited');
    default: return t('standingOrders.orderActions.errors.generic');
  }
}

// ---------------------------------------------------------------- the edit form

export interface EditForm {
  amount: string;
  /** yyyy-MM-dd, or '' for "leave the date as it is". */
  nextDate: string;
  remainingPayments: string;
  unlimited: boolean;
  comments: string;
}

export type EditField = 'amount' | 'nextDate' | 'payments' | 'comments';

export type EditError =
  | 'amountRequired'
  | 'amountIncrease'
  | 'nextDateNotFuture'
  | 'nextDateDayAfter28'
  | 'nextDateRequired'
  | 'paymentsBelowOne'
  | 'commentsTooLong';

export interface EditPlan {
  /** Only what changed. Empty when nothing did. */
  request: UpdateCreditStandingOrderRequest;
  changes: {
    amount?: { from: number; to: number };
    nextDate?: { from: Date | null; to: Date };
    payments?: { from: number | null; to: number };
    comments?: boolean;
  };
  errors: Partial<Record<EditField, EditError>>;
}

export const MAX_COMMENTS_LENGTH = 300;

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** yyyy-MM-dd in local time, as the date input gives it. */
function parseIsoDay(value: string): Date | null {
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!parts) return null;
  const date = new Date(+parts[1], +parts[2] - 1, +parts[3]);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * The form opens on the order as it is. A next date already behind us is left empty rather than
 * offered back, so it cannot be saved by accident; an ended order's payments are left empty too.
 */
export function initialEditForm(order: NedarimStandingOrderDtoIn, today: Date = new Date()): EditForm {
  const next = parseDate(order.nextDate);
  const unlimited = order.remainingPayments == null || isUnlimited(order.remainingPayments);
  const remaining = order.remainingPayments ?? 0;

  return {
    amount: String(order.amount),
    nextDate: next && next > startOfDay(today) ? formatIsoDate(next) : '',
    remainingPayments: unlimited || remaining <= 0 ? '' : String(remaining),
    unlimited,
    comments: order.comments ?? '',
  };
}

export function planEdit(order: NedarimStandingOrderDtoIn, form: EditForm, today: Date = new Date()): EditPlan {
  const plan: EditPlan = { request: {}, changes: {}, errors: {} };
  const todayStart = startOfDay(today);

  // Amount: down only (decided 4.10.2026).
  const amountText = form.amount.trim();
  const amount = Number(amountText);
  if (amountText === '' || !Number.isFinite(amount) || amount <= 0) {
    plan.errors.amount = 'amountRequired';
  } else {
    const rounded = Math.round(amount * 100) / 100;
    if (rounded > order.amount) plan.errors.amount = 'amountIncrease';
    else if (rounded !== order.amount) {
      plan.request.amount = rounded;
      plan.changes.amount = { from: order.amount, to: rounded };
    }
  }

  // Payments left: at least one, or until the donor stops.
  const currentPayments = order.remainingPayments;
  const currentlyUnlimited = currentPayments == null || isUnlimited(currentPayments);
  if (form.unlimited) {
    if (!currentlyUnlimited) {
      plan.request.unlimitedPayments = true;
      plan.changes.payments = { from: currentPayments, to: UNLIMITED_PAYMENTS };
    }
  } else {
    const text = form.remainingPayments.trim();
    const ended = (currentPayments ?? 0) <= 0 && !currentlyUnlimited;
    if (text === '') {
      // Empty means "as it is" only where the field opened empty: an ended order. Anywhere else
      // the coordinator has cleared a number, and an order with no payments is not an edit.
      if (!ended) plan.errors.payments = 'paymentsBelowOne';
    } else {
      const payments = Number(text);
      if (!Number.isInteger(payments) || payments < 1) plan.errors.payments = 'paymentsBelowOne';
      else {
        const target = Math.min(payments, UNLIMITED_PAYMENTS);
        if (target !== currentPayments) {
          plan.request.remainingPayments = target;
          plan.changes.payments = { from: currentPayments, to: target };
        }
      }
    }
  }

  // Next charge: tomorrow or later, day 1-28.
  const currentNext = parseDate(order.nextDate);
  if (form.nextDate) {
    const next = parseIsoDay(form.nextDate);
    if (!next || next <= todayStart) plan.errors.nextDate = 'nextDateNotFuture';
    else if (next.getDate() > 28) plan.errors.nextDate = 'nextDateDayAfter28';
    else if (!currentNext || next.getTime() !== startOfDay(currentNext).getTime()) {
      plan.request.nextDate = form.nextDate;
      plan.changes.nextDate = { from: currentNext, to: next };
    }
  }

  // An order given payments after it ended charges on its next date - which must be ahead of us.
  const revived = (currentPayments ?? 0) <= 0 && !currentlyUnlimited && plan.changes.payments !== undefined;
  if (revived && !plan.errors.nextDate) {
    const effective = plan.changes.nextDate?.to ?? currentNext;
    if (!effective || startOfDay(effective) <= todayStart) plan.errors.nextDate = 'nextDateRequired';
  }

  // The note, on the fund's own mosad only.
  if (!isSharedMosad(order)) {
    const comments = form.comments.trim();
    if (comments.length > MAX_COMMENTS_LENGTH) plan.errors.comments = 'commentsTooLong';
    else if (comments !== (order.comments ?? '').trim()) {
      plan.request.comments = comments;
      plan.changes.comments = true;
    }
  }

  return plan;
}

export const hasChanges = (plan: EditPlan): boolean => Object.keys(plan.request).length > 0;

export const hasErrors = (plan: EditPlan): boolean => Object.keys(plan.errors).length > 0;

const toNedarimDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

/**
 * The order as it reads after an action Nedarim accepted, so the board and the blade show it at
 * once instead of after the next read of the list - a few seconds in which the button just pressed
 * would still offer the opposite. The list is read again straight after and confirms it.
 */
export function applyAction(
  order: NedarimStandingOrderDtoIn,
  kind: Exclude<StandingOrderActionKind, 'cancel'>,
  result: StandingOrderActionResult,
  request?: UpdateCreditStandingOrderRequest,
): NedarimStandingOrderDtoIn {
  if (kind === 'freeze') return { ...order, enabled: 'Disabled' };

  if (kind === 'reactivate') return { ...order, enabled: 'Enabled', nextDate: result.nextDate ?? order.nextDate };

  const next = request?.nextDate ? parseIsoDay(request.nextDate) : null;
  return {
    ...order,
    amount: request?.amount ?? order.amount,
    nextDate: next ? toNedarimDate(next) : order.nextDate,
    remainingPayments: request?.unlimitedPayments
      ? UNLIMITED_PAYMENTS
      : request?.remainingPayments ?? order.remainingPayments,
    comments: request?.comments ?? order.comments,
  };
}

/** The before and after an edit recorded, as the log line reads it. */
export function parseEditDetails(details: string | null): {
  amount?: [number | null, number | null];
  nextDate?: [string | null, string | null];
  remainingPayments?: [number | null, number | null];
  comments?: [string | null, string | null];
  /** A bank order's freeze or cancellation (HAT-1441): the reason the coordinator gave. */
  reason?: [string | null, string | null];
} {
  if (!details) return {};
  try {
    return JSON.parse(details);
  } catch {
    return {};
  }
}

/**
 * What the declined-orders check wrote when it froze an order (HAT-1447): how many months in a row it
 * was refused, from and to (yyyy-MM), and the last refusal's wording. The first entries, written
 * before this shape, held the list of months instead; their count is its length. An unreadable value
 * reads as no months.
 */
export function parseAutoFreezeDetails(details: string | null): { count: number; from: string | null; to: string | null; lastError: string | null } {
  try {
    const parsed = details ? JSON.parse(details) : null;
    const months: unknown[] = Array.isArray(parsed?.months) ? parsed.months : [];
    return {
      count: typeof parsed?.count === 'number' ? parsed.count : months.length,
      from: typeof parsed?.from === 'string' ? parsed.from : null,
      to: typeof parsed?.to === 'string' ? parsed.to : null,
      lastError: typeof parsed?.lastError === 'string' ? parsed.lastError : null,
    };
  } catch {
    return { count: 0, from: null, to: null, lastError: null };
  }
}

/**
 * The listed order with the terms an edit is judged on - amount, next charge date, payments left -
 * taken from the live read of it (HAT-1382). The list is cached up to five minutes, so an amount
 * changed in Nedarim's own interface since is not in it; the form and the "only lower" rule
 * must start from what Nedarim holds. Without a live read the order is what the list says.
 */
export function withLiveTerms(order: NedarimStandingOrderDtoIn, live?: CreditStandingOrderDetailsDtoIn | null): NedarimStandingOrderDtoIn {
  if (!live || !(live.amount > 0)) return order;
  return {
    ...order,
    amount: live.amount,
    nextDate: live.nextDate || order.nextDate,
    remainingPayments: live.remainingPayments ?? order.remainingPayments,
  };
}
